import { useSyncExternalStore } from "react";
import { addDays, fromDay, startOfMonthDay, toDay, type ISODate } from "@/engine/date";
import { formatDate } from "@/lib/date-format";
import {
  shiftHomeMonth,
  type HomeDayCount,
  type HomeGate,
  type HomePlanHealthLevel,
  type HomePlanSummaryRow,
  type HomeSnapshot,
  type HomeTaskRow,
} from "@/features/home/home-model";

export const HOME_MOCK_CASES = [
  { id: "many", label: "많이" },
  { id: "few", label: "적게" },
  { id: "overdue", label: "지연" },
  { id: "steady", label: "기준 안" },
  { id: "empty", label: "없음" },
] as const;

export type HomeMockCase = "live" | (typeof HOME_MOCK_CASES)[number]["id"];

export interface HomeMockView {
  joined: number;
  snapshot: HomeSnapshot;
  gates: HomeGate[];
}

const LONG_TASK = "2026년 4분기 캠페인 키비주얼 최종 확정과 모바일 홈 상단 교체";
const LONG_PLAN = "2026년 4분기 그로스 캠페인과 파트너 공동 프로모션";

const MARKETING = { id: "mock-plan-marketing", name: "고객 마케팅 시스템" };
const ERP = { id: "mock-plan-erp", name: "제조사 ERP 구축" };
const PARTNER = { id: "mock-plan-partner", name: LONG_PLAN };
const CONTENT = { id: "mock-plan-content", name: "콘텐츠 운영" };

const MOCK_CASE_KEY = "home-mock-case";

function readStoredCase(): HomeMockCase {
  try {
    const raw = sessionStorage.getItem(MOCK_CASE_KEY);
    if (raw === "many" || raw === "few" || raw === "overdue" || raw === "steady" || raw === "empty" || raw === "live") return raw;
  } catch {
    // 이 환경에는 sessionStorage가 없다.
  }
  return "live";
}

let current: HomeMockCase = readStoredCase();
const listeners = new Set<() => void>();

export function getHomeMockCase(): HomeMockCase {
  return current;
}

export function setHomeMockCase(next: HomeMockCase) {
  if (current === next) return;
  current = next;
  try {
    sessionStorage.setItem(MOCK_CASE_KEY, next);
  } catch {
    // 저장하지 못해도 이번 화면에는 적용한다.
  }
  for (const listener of listeners) listener();
}

export function useHomeMockCase(): HomeMockCase {
  return useSyncExternalStore(
    (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    getHomeMockCase,
    getHomeMockCase,
  );
}

export function homeMock(scenario: Exclude<HomeMockCase, "live">, today: ISODate): HomeMockView {
  if (scenario === "empty") return { joined: 0, snapshot: assemble([], [], 0, today), gates: [] };
  if (scenario === "few") {
    const plans = [plan(MARKETING, "watch", addDays(today, 40), 46, "기준선 +4일")];
    return {
      joined: plans.length,
      snapshot: assemble([task(MARKETING, "few-today", "킥오프 참석자 확정", today, false)], plans, 1, today),
      gates: [gate(MARKETING, "milestone", "few-open", "오픈", onMonth(today, 0, 28), today)],
    };
  }
  if (scenario === "overdue") {
    const plans = [plan(ERP, "risk", addDays(today, -12), 31, "종료일 경과 · SPI 0.58 · 마감 초과 4건")];
    const tasks = [
      task(ERP, "late-1", "인터페이스 명세", addDays(today, -2), true),
      task(ERP, "late-2", "권한 매트릭스", addDays(today, -6), true),
      task(ERP, "late-3", "전환 리허설", addDays(today, -11), true),
      task(ERP, "late-4", LONG_TASK, addDays(today, -18), true),
    ];
    return {
      joined: plans.length,
      snapshot: assemble(tasks, plans, tasks.length, today),
      gates: [gate(ERP, "deadline", "prev-cut", "데이터 전환", onMonth(today, -1, 12), today)],
    };
  }
  if (scenario === "steady") {
    const plans = [
      plan(MARKETING, "good", addDays(today, 50), 72, "SPI 1.04 · 정상"),
      plan(CONTENT, "good", addDays(today, 30), 64, "SPI 1.01 · 정상"),
      plan(ERP, "good", addDays(today, 80), 18, "기준선 미저장"),
    ];
    const tasks = [
      task(MARKETING, "steady-today", "주간 실험 정리", today, false),
      task(CONTENT, "steady-tomorrow", "콘텐츠 캘린더", addDays(today, 1), false),
      task(MARKETING, "steady-later", "세그먼트 정의", addDays(today, 4), false),
    ];
    return {
      joined: plans.length,
      snapshot: assemble(tasks, plans, 8, today),
      gates: [
        gate(MARKETING, "milestone", "steady-done", "브랜드 확정", onMonth(today, 0, 4), today, true),
        gate(CONTENT, "milestone", "steady-next", "발행", onMonth(today, 0, 28), today),
      ],
    };
  }
  const plans = [
    plan(ERP, "risk", addDays(today, -8), 34, "종료일 경과 · SPI 0.62 · 마감 초과 4건"),
    plan(MARKETING, "watch", addDays(today, 24), 51, "기준선 +6일 · SPI 0.91"),
    plan(PARTNER, "watch", addDays(today, 18), 22, "막힘 2건"),
    plan(CONTENT, "good", addDays(today, 60), 71, "SPI 1.05 · 정상"),
  ];
  const tasks = [
    task(ERP, "many-late-1", LONG_TASK, addDays(today, -1), true),
    task(ERP, "many-late-2", "권한 매트릭스", addDays(today, -4), true),
    task(MARKETING, "many-late-3", "매체 견적 확정", addDays(today, -9), true),
    task(PARTNER, "many-late-4", "계약 조항 회신", addDays(today, -15), true),
    task(MARKETING, "many-today-1", "실험 결과 공유", today, false),
    task(CONTENT, "many-today-2", "발행 캘린더", today, false),
    task(ERP, "many-tomorrow", "인터페이스 리뷰", addDays(today, 1), false),
    task(PARTNER, "many-later-1", "공동 랜딩 카피", addDays(today, 3), false),
    task(CONTENT, "many-later-2", "채널 배분", addDays(today, 6), false),
  ];
  return {
    joined: plans.length,
    snapshot: assemble(tasks, plans, 28, today),
    gates: [
      gate(ERP, "deadline", "many-cut", "데이터 전환", onMonth(today, 0, 2), today),
      gate(MARKETING, "milestone", "many-done", "브랜드 확정", onMonth(today, 0, 8), today, true),
      gate(PARTNER, "milestone", "many-late", LONG_TASK, onMonth(today, 0, 16), today),
      gate(CONTENT, "deadline", "many-ship", "발행", onMonth(today, 0, 28), today),
      gate(ERP, "milestone", "many-prev", "설계 승인", onMonth(today, -1, 20), today),
      gate(MARKETING, "milestone", "many-next", "캠페인 오픈", onMonth(today, 1, 6), today),
    ],
  };
}

function onMonth(today: ISODate, shift: -1 | 0 | 1, day: number): ISODate {
  const first = shift === 0 ? fromDay(startOfMonthDay(toDay(today))) : shiftHomeMonth(today, shift);
  return addDays(first, day - 1);
}

function task(plan: { id: string; name: string }, taskId: string, title: string, due: ISODate, overdue: boolean): HomeTaskRow {
  return { projectId: plan.id, projectName: plan.name, taskId, title, due, overdue };
}

function plan(owner: { id: string; name: string }, level: HomePlanHealthLevel, finish: ISODate, progress: number, reason: string): HomePlanSummaryRow {
  const risk = level === "risk";
  return {
    id: owner.id,
    name: owner.name,
    level,
    finish,
    progress,
    spi: level === "good" && reason.startsWith("기준선") ? null : risk ? 0.62 : level === "watch" ? 0.91 : 1.04,
    varianceDays: risk ? 12 : level === "watch" ? 6 : 0,
    hasBaseline: !reason.includes("기준선 미저장"),
    blockedCount: reason.includes("막힘") ? 2 : 0,
    overdueCount: risk ? 4 : 0,
    reason,
  };
}

function gate(plan: { id: string; name: string }, kind: HomeGate["kind"], refId: string, title: string, date: ISODate, today: ISODate, done = false): HomeGate {
  return { key: `${plan.id}:${kind}:${refId}`, projectId: plan.id, projectName: plan.name, kind, refId, title, date, late: !done && date < today, done };
}

function assemble(tasks: HomeTaskRow[], allPlans: HomePlanSummaryRow[], mineOpen: number, today: ISODate): HomeSnapshot {
  const levelRank: Record<HomePlanHealthLevel, number> = { risk: 0, watch: 1, good: 2 };
  const sorted = [...allPlans].sort((a, b) => levelRank[a.level] - levelRank[b.level] || a.finish.localeCompare(b.finish) || a.name.localeCompare(b.name, "ko"));
  const plans = sorted.filter((row): row is HomePlanSummaryRow & { level: "risk" | "watch" } => row.level !== "good");
  const overdue = tasks.filter((row) => row.overdue).length;
  const weekDays: HomeDayCount[] = [
    { key: "overdue", date: "overdue", label: "지연", calloutTitle: "기한 지남", count: overdue, isOverdue: true },
    ...Array.from({ length: 8 }, (_, index) => {
      const date = addDays(today, index);
      const count = tasks.filter((row) => !row.overdue && row.due === date).length;
      return {
        key: date,
        date,
        label: index === 0 ? "오늘" : formatDate(date, "short"),
        calloutTitle: `${formatDate(date, "fullWeekday")}${index === 0 ? " (오늘)" : ""}`,
        count,
        isOverdue: false,
      };
    }),
  ];
  return {
    overdue,
    dueThisWeek: tasks.filter((row) => !row.overdue).length,
    attentionPlans: plans.length,
    mineOpen,
    tasks,
    plans,
    allPlans: sorted,
    planCounts: {
      good: sorted.filter((row) => row.level === "good").length,
      watch: sorted.filter((row) => row.level === "watch").length,
      risk: sorted.filter((row) => row.level === "risk").length,
      total: sorted.length,
    },
    weekDays,
  };
}
